import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Box } from "@mui/material";
import { useHistory } from "react-router-dom";
import { AiFillAppstore } from "react-icons/ai";
import { AddElementsButton } from "../../../../Components/AddElementsButton";
import { deleteAlert, errorAlert } from "../../../../Components/Alerts";
import { ApiLink } from "../../../../APILink/ApiLink";
import authHeader from "../../../../Auth/authHeader";
import { FiscalYearContext } from "../../../../FiscalYear/FiscalYearContext";
import { produceTransactionGrid } from "./ProduceGrid";
import CrudPageHeader from "../../../../Components/CrudPageHeader";
import ReusableServerDataGrid from "../../../../Components/ReusableServerDataGrid";
import StandardProduceViewModal from "./StandardProduceViewModal";
import "./StandardProducePage.scss";

const formatDate = (value) => {
  if (!value) {
    return "";
  }

  return new Date(value).toLocaleDateString("en-GB").replace(/\//g, ".");
};

export default function StandardProducePage() {
  const history = useHistory();
  const headers = authHeader();
  const { fiscalYear } = useContext(FiscalYearContext);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });
  const [sortModel, setSortModel] = useState([]);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [quickFilter, setQuickFilter] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewData, setViewData] = useState(null);

  const getFieldValue = (obj, keys, fallback = null) => {
    if (!obj) {
      return fallback;
    }

    for (const key of keys) {
      if (obj[key] !== undefined && obj[key] !== null) {
        return obj[key];
      }
    }

    return fallback;
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${ApiLink.API_URL}StandardProduce/transactionsGrid`, {
        headers,
        params: {
          fiscalYear,
          page: paginationModel.page,
          pageSize: paginationModel.pageSize,
          sort: JSON.stringify(sortModel),
          filter: JSON.stringify(filterModel.items),
          quickFilter: quickFilter.join(" "),
        },
      });

      const items = response?.data?.items || [];
      setData(items);
      setRowCount(response?.data?.total || 0);
    } catch (error) {
      errorAlert(
        error?.response?.data?.message ||
          error?.response?.data?.errorMessage ||
          error?.message ||
          "Gabim në marrjen e fletëve të prodhimit."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [fiscalYear, paginationModel, sortModel, filterModel, quickFilter]);

  const handleDeleteProduce = async (transactionId) => {
    try {
      await axios.delete(`${ApiLink.API_URL}Produce/deleteTransactions/${transactionId}`, { headers });
      deleteAlert("Fletë prodhimi është fshirë me sukses");
      fetchData();
    } catch (error) {
      errorAlert(
        error?.response?.data?.message ||
          error?.response?.data?.errorMessage ||
          "Fletë prodhimi nuk është fshirë"
      );
    }
  };

  const handleOpenCreatePage = () => {
    history.push("/produc/newProduce");
  };

  const handleOpenFromGrid = (rowItem) => {
    history.push({
      pathname: "/produc/newProduce",
      state: {
        mode: "edit",
        transaction: rowItem,
      },
    });
  };

  const handleView = async (item) => {
    const rowItem = item?.rowInfo || item;
    const transactionID = Number(rowItem?.TransactionID || 0);

    if (!transactionID) {
      errorAlert("Nuk u gjet ID e fletës së prodhimit për view.");
      return;
    }

    setShowViewModal(true);
    setViewLoading(true);

    try {
      const producedResponse = await axios.get(`${ApiLink.API_URL}StandardProduce/producedSummary`, {
        headers,
        params: { transactionID },
      });

      const producedItem = producedResponse?.data || null;
      const producedArticleID = Number(
        getFieldValue(producedItem, ["ArticlesID", "articlesID", "ArticleID", "articleID"], 0)
      );
      const producedQty = Number(
        getFieldValue(producedItem, ["Qty", "qty", "QtyInput", "qtyInput"], 0)
      );

      let components = [];
      if (producedArticleID > 0) {
        const receptureResponse = await axios.get(`${ApiLink.API_URL}StandardProduce/receptureDetails`, {
          headers,
          params: { compoundArticleID: producedArticleID },
        });

        components = (receptureResponse?.data || []).map((component) => {
          const qtyPerUnit = Number(component?.Qty || 0);
          const qty = producedQty > 0 ? qtyPerUnit * producedQty : qtyPerUnit;
          const price = Number(component?.CmimiMesatar || 0);
          return {
            ...component,
            QtyPerUnit: qtyPerUnit,
            Qty: qty,
            TotalAmount: qty * price,
          };
        });
      }

      setViewData({
        transactionID,
        invoiceNr: rowItem?.InvoiceNr || "",
        date: rowItem?.Date || "",
        comment: rowItem?.Coment || "",
        total: Number(rowItem?.Total || 0),
        produced: {
          articleID: producedArticleID,
          articleName: getFieldValue(producedItem, ["ArticleName", "articleName"], "-"),
          barcode: getFieldValue(producedItem, ["Barcode", "barcode"], "-"),
          unitName: getFieldValue(producedItem, ["UnitName", "unitName"], "-"),
          qty: producedQty,
          price: Number(getFieldValue(producedItem, ["CmimiMesatar", "cmimiMesatar"], 0)),
          totalAmount: Number(getFieldValue(producedItem, ["TotalAmount", "totalAmount", "Total", "total"], 0)),
        },
        components,
      });
    } catch (error) {
      setShowViewModal(false);
      errorAlert(error);
    } finally {
      setViewLoading(false);
    }
  };

  const rows = useMemo(
    () =>
      (data || []).map((item, index) => ({
        id: item.TransactionID || paginationModel.page * paginationModel.pageSize + index + 1,
        inv: `PRD-${fiscalYear}-${item.InvoiceNr}`,
        date: formatDate(item.Date),
        client: item.Coment,
        total: item.Total,
        deleteItemId: item.TransactionID,
        rowInfo: item,
      })),
    [data, fiscalYear, paginationModel.page, paginationModel.pageSize]
  );

  return (
    <div className="Crud__Container SPP__page">
      <div className="SPP__content">
        <CrudPageHeader
          icon={AiFillAppstore}
          title="Prodhimi"
          description="Lista e prodhimit. Për krijim ose editim, hapet forma e dedikuar."
          tutorialUrl="https://www.youtube.com/@Accountree"
          tutorialLabel="Shiko Tutorialin"
          className="SPP__header"
          action={
            <Box className="SPP__header-actions">
              <AddElementsButton
                text="Krijo Prodhim"
                icon="bi bi-plus-circle"
                className="button-68"
                onClick={handleOpenCreatePage}
              />
            </Box>
          }
        />

        <ReusableServerDataGrid
          columns={produceTransactionGrid}
          rows={rows}
          onView={handleView}
          onEdit={(item) => handleOpenFromGrid(item.rowInfo || item)}
          onDelete={handleDeleteProduce}
          showViewAction={true}
          showPayment={false}
          height={620}
          rowCount={rowCount}
          loading={loading}
          paginationModel={paginationModel}
          setPaginationModel={setPaginationModel}
          sortModel={sortModel}
          setSortModel={setSortModel}
          filterModel={filterModel}
          setFilterModel={setFilterModel}
          quickFilter={quickFilter}
          setQuickFilter={setQuickFilter}
        />

        <StandardProduceViewModal
          show={showViewModal}
          onHide={() => {
            setShowViewModal(false);
            setViewData(null);
          }}
          data={viewData}
          loading={viewLoading}
          fiscalYear={fiscalYear}
        />
      </div>
    </div>
  );
}
